export const appBuildInfo = {
  version: __APP_VERSION__,
  buildRef: __APP_BUILD_REF__,
  commitSha: __APP_SHA__,
  builtAtUtc: __APP_BUILT_AT_UTC__,
  runId: __APP_RUN_ID__,
};

interface BuildTimeFormatOptions {
  locale?: string;
  timeZone?: string;
}

function padDatePart(value: string | undefined): string {
  return value?.padStart(2, "0") ?? "00";
}

export function formatVersionForDisplay(version: string): string {
  const localBuildMatch = version.match(/^(.+\+(?:b)?local\.[a-z0-9]{3})/i);
  if (localBuildMatch) {
    return localBuildMatch[1];
  }

  return version;
}

export function formatBuiltAtForDisplay(
  builtAtUtc: string,
  { locale = undefined, timeZone }: BuildTimeFormatOptions = {},
): string {
  const builtAt = new Date(builtAtUtc);

  if (Number.isNaN(builtAt.getTime())) {
    return builtAtUtc;
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
    timeZoneName: "short",
  });
  const parts = formatter.formatToParts(builtAt);
  const day = padDatePart(parts.find((part) => part.type === "day")?.value);
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const hour = padDatePart(parts.find((part) => part.type === "hour")?.value);
  const minute = padDatePart(parts.find((part) => part.type === "minute")?.value);
  const timeZoneName = parts.find((part) => part.type === "timeZoneName")?.value ?? "";

  return `${[day, month, year].filter(Boolean).join(" ")}, ${hour}:${minute}${timeZoneName ? ` ${timeZoneName}` : ""}`;
}

export function getSentryFeedbackContextLine(options?: BuildTimeFormatOptions): string {
  return `Version ${formatVersionForDisplay(appBuildInfo.version)} | Built ${formatBuiltAtForDisplay(appBuildInfo.builtAtUtc, options)}`;
}

export function getSentryFeedbackConfig(options?: BuildTimeFormatOptions) {
  const feedbackContext = getSentryFeedbackContextLine(options);

  return {
    formTitle: "Share feedback with the Supply Guard team",
    messageLabel: "What should we improve?",
    messagePlaceholder: "Describe what happened, what you expected, and the page or workflow you were using.",
    submitButtonLabel: "Submit feedback",
    triggerLabel: "Send feedback",
    tags: {
      app_version: appBuildInfo.version,
      build_ref: appBuildInfo.buildRef,
      built_at_local: formatBuiltAtForDisplay(appBuildInfo.builtAtUtc, options),
      built_at_utc: appBuildInfo.builtAtUtc,
      run_id: appBuildInfo.runId,
      feedback_context: feedbackContext,
    },
  };
}

export function formatBuildInfoForClipboard(): string {
  return JSON.stringify(
    {
      app: "supplyspark-ent",
      version: appBuildInfo.version,
      buildRef: appBuildInfo.buildRef,
      commitSha: appBuildInfo.commitSha,
      runId: appBuildInfo.runId,
      builtAtUtc: appBuildInfo.builtAtUtc,
    },
    null,
    2,
  );
}
